import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "../store";
import {
  internalSearchWorkflow,
  searchWorkflow,
  searchWorkflowTemplates,
  SearchWorkflowRequest,
  toggleWorkflowStatus,
  ToggleWorkflowStatusInput,
  updateWorkflow,
  Workflow,
  WorkflowDefinitionFilter
} from "src/network/graphql/workflowService";
import { searchWallet } from "src/network/graphql/walletService";
import { getTriggerErrorMessage } from "src/utils/errors";
import { ClientOptions } from "src/network/client";
import {
  WORKFLOW_SEARCH_PAGE_SIZE,
  WorkflowGroupFields,
  WorkflowOrderUpdate,
  nextPriorityInGroup
} from "./workflowGrouping";

export type WorkflowListMode = "all" | "templates";

export const SYSTEM_ORGANISATION_ID = "SYSTEM";

export interface WorkflowWalletOption {
  id: string;
  name: string;
  balance?: number;
}

interface RawSearchWalletItem {
  wallet_id: string | number;
  name: string;
  balance?: number;
}

export const toWorkflowWalletOptions = (items: RawSearchWalletItem[]): WorkflowWalletOption[] =>
  items.map((w) => ({ id: String(w.wallet_id), name: w.name, balance: w.balance }));

// One object-type tab of the Custom list (first 100 rules).
export interface WorkflowTabList {
  items: Workflow[];
  total: number;
  loaded: boolean;
  loading: boolean;
  error: unknown;
}

// Org workflows created from one template (source_template_id).
export interface TemplateUsage {
  total: number;
  active: number;
}

interface WorkflowsState {
  list: Workflow[];
  total: number;
  listLoading: boolean;
  listError: unknown;
  toggleLoading: Record<string, boolean>;
  tabLists: Record<string, WorkflowTabList>;
  orderSaving: boolean;
  walletCache: Record<string, WorkflowWalletOption>;
  templates: Workflow[];
  templatesLoading: boolean;
  templatesError: unknown;
  templateLinks: Record<string, TemplateUsage>;
  templateLinksLoading: boolean;
}

const initialState: WorkflowsState = {
  list: [],
  total: 0,
  listLoading: false,
  listError: null,
  toggleLoading: {},
  tabLists: {},
  orderSaving: false,
  walletCache: {},
  templates: [],
  templatesLoading: false,
  templatesError: null,
  templateLinks: {},
  templateLinksLoading: false
};

const workflowsSlice = createSlice({
  name: "workflows",
  initialState,
  reducers: {
    fetchListStart: (state) => {
      state.listLoading = true;
      state.listError = null;
    },
    fetchListSuccess: (state, action: PayloadAction<{ items: Workflow[]; total: number }>) => {
      state.listLoading = false;
      state.list = action.payload.items;
      state.total = action.payload.total;
    },
    fetchListFail: (state, action) => {
      state.listLoading = false;
      state.list = [];
      state.total = 0;
      state.listError = action.payload;
    },
    tabListsReset: (state) => {
      state.tabLists = {};
    },
    tabFetchStart: (state, action: PayloadAction<string>) => {
      const current = state.tabLists[action.payload];

      state.tabLists[action.payload] = {
        items: current?.items ?? [],
        total: current?.total ?? 0,
        loaded: Boolean(current?.loaded),
        loading: true,
        error: null
      };
    },
    tabFetchSuccess: (state, action: PayloadAction<{ entity_type: string; items: Workflow[]; total: number }>) => {
      const { entity_type, items, total } = action.payload;

      state.tabLists[entity_type] = { items, total, loaded: true, loading: false, error: null };
    },
    tabFetchFail: (state, action: PayloadAction<{ entity_type: string; error: unknown }>) => {
      state.tabLists[action.payload.entity_type] = {
        items: [],
        total: 0,
        loaded: false,
        loading: false,
        error: action.payload.error
      };
    },
    orderSaveStart: (state) => {
      state.orderSaving = true;
    },
    orderSaveFinish: (state) => {
      state.orderSaving = false;
    },
    upsertWorkflowInList: (state, action: PayloadAction<Workflow>) => {
      const idx = state.list.findIndex((w) => w.workflow_id === action.payload.workflow_id);

      if (idx >= 0) state.list[idx] = action.payload;
    },
    toggleStart: (state, action: PayloadAction<string>) => {
      state.toggleLoading[action.payload] = true;
    },
    toggleFinish: (state, action: PayloadAction<string>) => {
      state.toggleLoading[action.payload] = false;
    },
    cacheWorkflowWallets: (state, action: PayloadAction<WorkflowWalletOption[]>) => {
      action.payload.forEach((option) => {
        state.walletCache[option.id] = option;
      });
    },
    templatesFetchStart: (state) => {
      state.templatesLoading = true;
      state.templatesError = null;
    },
    templatesFetchSuccess: (state, action: PayloadAction<Workflow[]>) => {
      state.templatesLoading = false;
      state.templates = action.payload;
    },
    templatesFetchFail: (state, action) => {
      state.templatesLoading = false;
      state.templates = [];
      state.templatesError = action.payload;
    },
    templateLinksFetchStart: (state) => {
      state.templateLinksLoading = true;
    },
    templateLinksSuccess: (state, action: PayloadAction<Record<string, TemplateUsage>>) => {
      state.templateLinksLoading = false;
      state.templateLinks = action.payload;
    },
    templateLinksFetchFail: (state) => {
      state.templateLinksLoading = false;
      state.templateLinks = {};
    }
  }
});

export const {
  fetchListStart,
  fetchListSuccess,
  fetchListFail,
  tabListsReset,
  tabFetchStart,
  tabFetchSuccess,
  tabFetchFail,
  orderSaveStart,
  orderSaveFinish,
  upsertWorkflowInList,
  toggleStart,
  toggleFinish,
  cacheWorkflowWallets,
  templatesFetchStart,
  templatesFetchSuccess,
  templatesFetchFail,
  templateLinksFetchStart,
  templateLinksSuccess,
  templateLinksFetchFail
} = workflowsSlice.actions;

export const resolveWorkflowWalletsAction = (ids: string[]) => {
  return async (dispatch: StoreDispatch) => {
    if (!ids.length) return;
    try {
      const response = await searchWallet({ size: ids.length, from: 0, walletFilter: { wallet_ids: ids } });

      dispatch(cacheWorkflowWallets(toWorkflowWalletOptions(response?.data?.search_wallet?.items ?? [])));
    } catch {
      // errors surface via the client interceptor toast
    }
  };
};

export interface FetchWorkflowListParams extends SearchWorkflowRequest {
  mode: WorkflowListMode;
}

export const fetchWorkflowListAction = (input: FetchWorkflowListParams) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchListStart());
    try {
      const response = await internalSearchWorkflow({
        from: input.from,
        size: input.size,
        sort_by: input.sort_by,
        sort: input.sort,
        workflowFilter: {
          ...(input.workflowFilter ?? {}),
          organisation_id: input.mode === "templates" ? SYSTEM_ORGANISATION_ID : undefined
        }
      });
      const payload: { items?: Workflow[]; total_size?: number } | undefined = response?.data?.internal_search_workflow;

      dispatch(
        fetchListSuccess({
          items: payload?.items ?? [],
          total: payload?.total_size ?? 0
        })
      );
      return payload;
    } catch (error) {
      dispatch(fetchListFail(error));
      immediateToast("error", {
        message: (error as Error)?.message || "Failed to load workflows",
        timeout: 3000,
        position: "topCenter"
      });
      return null;
    }
  };
};

export interface FetchWorkflowTabParams {
  entity_type: string;
  workflowFilter?: Omit<WorkflowDefinitionFilter, "entity_type" | "entity_types">;
}

export const fetchWorkflowTabAction = ({ entity_type, workflowFilter }: FetchWorkflowTabParams) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(tabFetchStart(entity_type));
    try {
      const response = await searchWorkflow({
        from: 0,
        size: WORKFLOW_SEARCH_PAGE_SIZE,
        workflowFilter: { ...workflowFilter, entity_type }
      });
      const payload: { items?: Workflow[]; total_size?: number } | undefined = response?.data?.search_workflow;

      dispatch(tabFetchSuccess({ entity_type, items: payload?.items ?? [], total: payload?.total_size ?? 0 }));
    } catch (error) {
      dispatch(tabFetchFail({ entity_type, error }));
      immediateToast("error", {
        message: (error as Error)?.message || "Failed to load workflows",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

// Sequential so each write uses the row's current version; the first
// failure stops the run. cb runs either way so the caller can refetch.
export const saveWorkflowOrderAction = (updates: WorkflowOrderUpdate[], cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(orderSaveStart());
    for (const update of updates) {
      try {
        await updateWorkflow(update.input, { suppressErrorToast: true });
      } catch (error) {
        immediateToast("error", {
          message:
            `Couldn't save the order: "${update.workflow.name}" failed to update. ${getTriggerErrorMessage(error, "")}`.trim(),
          timeout: 5000,
          position: "topCenter"
        });
        dispatch(orderSaveFinish());
        cb?.();
        return false;
      }
    }
    immediateToast("success", { message: "Order saved", timeout: 3000, position: "topCenter" });
    dispatch(orderSaveFinish());
    cb?.();
    return true;
  };
};

export const toggleWorkflowStatusAction = (
  input: ToggleWorkflowStatusInput,
  cb?: (_workflow: Workflow) => void,
  fcb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(toggleStart(input.workflow_id));
    try {
      const response = await toggleWorkflowStatus(input);
      const workflow: Workflow | undefined = response?.data?.toggle_workflow_status;

      dispatch(toggleFinish(input.workflow_id));
      if (workflow) {
        dispatch(upsertWorkflowInList(workflow));
        immediateToast("success", {
          message: workflow.active ? "Workflow activated" : "Workflow deactivated",
          timeout: 3000,
          position: "topCenter"
        });
        cb?.(workflow);
      }
      return workflow ?? null;
    } catch (error) {
      dispatch(toggleFinish(input.workflow_id));
      fcb?.();
      return null;
    }
  };
};

export const fetchPredefinedTemplatesAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(templatesFetchStart());
    try {
      // activeOnly keeps template-level retirement: the catalogue lists only
      // templates the platform still offers.
      const response = await searchWorkflowTemplates({ activeOnly: true });
      const items: Workflow[] = response?.data?.search_workflow?.items ?? [];

      dispatch(templatesFetchSuccess(items));
      return items;
    } catch (error) {
      dispatch(templatesFetchFail(error));
      immediateToast("error", {
        message: (error as Error)?.message || "Failed to load predefined workflows",
        timeout: 3000,
        position: "topCenter"
      });
      return null;
    }
  };
};

const toTemplateUsageMap = (items: Workflow[]): Record<string, TemplateUsage> => {
  const usage: Record<string, TemplateUsage> = {};

  items.forEach((w) => {
    if (!w.source_template_id) return;
    const current = usage[w.source_template_id] ?? { total: 0, active: 0 };

    usage[w.source_template_id] = {
      total: current.total + 1,
      active: w.active ? current.active + 1 : current.active
    };
  });
  return usage;
};

// search_workflow caps size server-side, so page until every matching org workflow is read.
const searchAllOrgWorkflows = async (
  workflowFilter?: WorkflowDefinitionFilter,
  options?: ClientOptions
): Promise<Workflow[]> => {
  const items: Workflow[] = [];
  let from = 0;
  let total = 0;

  do {
    const response = await searchWorkflow({ from, size: WORKFLOW_SEARCH_PAGE_SIZE, workflowFilter }, options);
    const page: Workflow[] = response?.data?.search_workflow?.items ?? [];

    total = response?.data?.search_workflow?.total_size ?? 0;
    items.push(...page);
    if (page.length < WORKFLOW_SEARCH_PAGE_SIZE) break;
    from += WORKFLOW_SEARCH_PAGE_SIZE;
  } while (from < total);
  return items;
};

// Priority for a rule about to be created: after the last rule it competes with.
// Throws when the lookup fails — the caller blocks the save rather than guess.
export const fetchNextWorkflowPriority = async (candidate: WorkflowGroupFields): Promise<number> => {
  const existing = await searchAllOrgWorkflows(
    { subsystem: candidate.subsystem, entity_type: candidate.entity_type },
    { suppressErrorToast: true }
  );

  return nextPriorityInGroup(candidate, existing);
};

export const fetchTemplateLinksAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(templateLinksFetchStart());
    try {
      const links = toTemplateUsageMap(await searchAllOrgWorkflows());

      dispatch(templateLinksSuccess(links));
      return links;
    } catch (error) {
      dispatch(templateLinksFetchFail());
      immediateToast("error", {
        message: (error as Error)?.message || "Failed to load template usage",
        timeout: 3000,
        position: "topCenter"
      });
      return null;
    }
  };
};

export default workflowsSlice.reducer;
